import { createSelector } from 'reselect';
import { getUserRetirementFunds } from '../funds/UserFunds/userFunds.selectors';
import { AGREEMENT_TYPES } from '../../../settings/settings';
import { getAvailableFundList } from '../shoppingCart/shoppingCart.selectors';
import { Fund } from 'src/components/Exchange/types';
import { getIsRMDEligible } from '../RMD/RMDSetting.selectors';

/*
 Checks the product type of non-roth Retirement funds and returns a boolean indicating
 if any of those funds are APP. This is used to show the RMD tile on the transaction landing
 page within the APP section.
*/
export const getHasAPPRetirementFunds = createSelector(
  [getUserRetirementFunds],
  (userRetirementFunds) => {
    // Only check funds that are Traditional IRA or Rollover
    const agreementTypes = Object.keys(userRetirementFunds).filter(
      (agreementType) => agreementType !== AGREEMENT_TYPES.ROTH_IRA
    );
    // Check to see which funds are ManagedAccount
    const hasAPPRetirementFund = agreementTypes.some((agreementType) => {
      return userRetirementFunds[agreementType].some(
        ({ productType }) => productType === 'ManagedAccount'
      );
    });
    return hasAPPRetirementFund;
  }
);

/**
 * Checks the product type of non-roth Retirement funds and returns a boolean indicating i
 * any of those funds are Mutual Funds. This is used to show the RMD tile on the transaction landing
 * page within the Mutual Funds section.
 */
export const getHasMFRetirementFunds = createSelector(
  [getUserRetirementFunds],
  (userRetirementFunds) => {
    // Only check funds that are Traditional IRA or Rollover
    const agreementTypes = Object.keys(userRetirementFunds).filter(
      (agreementType) => agreementType !== AGREEMENT_TYPES.ROTH_IRA
    );
    // Check to see which funds are MutualFund
    const hasMFRetirementFund = agreementTypes.some((agreementType) => {
      return userRetirementFunds[agreementType].some(
        ({ productType }) => productType === 'MutualFund'
      );
    });
    return hasMFRetirementFund;
  }
);

/* Checks if the user is RMD eligible and they have an RMD eligible APP fund */
export const getIsRMDEligibleAPP = createSelector(
  [getIsRMDEligible, getHasAPPRetirementFunds],
  (isRMDEligible, hasAPPRetirementFunds) => {
    return isRMDEligible && hasAPPRetirementFunds;
  }
);

/* Checks if the user is RMD eligible and they have an RMD eligible Mutual Fund */
export const getIsRMDEligibleMF = createSelector(
  [getIsRMDEligible, getHasMFRetirementFunds],
  (isRMDEligible, hasMFRetirementFunds) => {
    return isRMDEligible && hasMFRetirementFunds;
  }
);

/* Gets all sweep accounts for a user. Used in the 'sell from sweep' modal in the transaction landing page. */
export const getUserSweepAccounts = createSelector(
  [getAvailableFundList],
  (funds: Fund[]) => {
    return funds.filter(({ sweep }) => sweep);
  }
);

/* Checks if the user has multiple sweep accounts. Used to determine if the user needs to select a sweep account
on the transaction landing page. */
export const getHasMultipleSweepAccounts = createSelector(
  [getUserSweepAccounts],
  (userSweepAccounts) => {
    return userSweepAccounts.length > 1;
  }
);

/* Returns a single sweep account for the user. Used in transaction landing page when a user only has 1 sweep account */
export const getSingleUserSweepAccount = createSelector(
  [getUserSweepAccounts],
  (userSweepAccounts) => {
    return userSweepAccounts?.[0] || {};
  }
);
